import { pdfImageColumnsQuery, readPdfImageColumnsPreference, } from '@/lib/report/pdfImageColumns';
import { ApiError, apiFetch, apiRequest, apiUpload, getAccessToken, resolveApiBase } from './client';
const BASE = '/teleconsultation/reports';
// ============ CRUD ============
export async function getTcReport(caseId) {
    try {
        return await apiFetch(`${BASE}?caseId=${encodeURIComponent(caseId)}`);
    }
    catch (e) {
        if (e instanceof ApiError && e.status === 404)
            return null;
        throw e;
    }
}
export async function getTcReportById(reportId) {
    try {
        return await apiFetch(`${BASE}/${reportId}`);
    }
    catch (e) {
        if (e instanceof ApiError && e.status === 404)
            return null;
        throw e;
    }
}
export async function createTcReport(data) {
    return await apiRequest('POST', BASE, {
        caseId: data.caseId,
        templateId: data.templateId,
        status: data.status,
        content: data.content,
        images: data.images ?? [],
    });
}
export async function updateTcReport(reportId, data) {
    await apiRequest('PUT', `${BASE}/${reportId}`, {
        status: data.status,
        content: data.content,
        images: data.images,
        // undefined = leave unchanged; '' = clear the stored template.
        templateId: data.templateId,
    });
}
/**
 * `stats` is the OPTIONAL dictation enrichment the backend rides along on its
 * `report.signed` analytics event (report_type 'teleconsultation'). Same shape and
 * same rule as the standard finalize: omitted entirely when the caller has no stats,
 * because absence of the keys — not false/0 — is what marks "old client" server-side.
 */
export async function finalizeTcReport(reportId, stats) {
    return await apiRequest('POST', `${BASE}/${reportId}/finalize`, stats
        ? { dictated: stats.dictated, dictWords: stats.dictWords, refineUsed: stats.refineUsed }
        : undefined);
}
export async function submitTcPreliminary(reportId) {
    await apiRequest('POST', `${BASE}/${reportId}/submit-preliminary`);
}
export async function addTcReportAddendum(reportId, reason) {
    return await apiRequest('POST', `${BASE}/${reportId}/addendum`, { reason });
}
export async function unfinalizeTcReport(reportId) {
    await apiRequest('POST', `${BASE}/${reportId}/unfinalize`);
}
export async function getTcReportsByCase(caseId) {
    return await apiFetch(`${BASE}/by-case?caseId=${encodeURIComponent(caseId)}`);
}
// ============ PDF ============
/**
 * Fetch the TC report PDF as a Blob (a caller that needs a URL creates and
 * revokes its own). `imageColumns` defaults to the signed-in user's
 * `pdfImageColumns` preference (shared cache), and is left off when that is
 * unset so the server follows the report design; see the standard-reports
 * counterpart.
 */
export async function getTcReportPdfBlob(reportId, imageColumns) {
    const token = getAccessToken();
    const headers = {};
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const columns = imageColumns ?? readPdfImageColumnsPreference();
    const response = await fetch(`${resolveApiBase(BASE)}${BASE}/${reportId}/pdf${pdfImageColumnsQuery(columns)}`, {
        headers,
        credentials: 'include',
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'PDF generation failed'));
    }
    return await response.blob();
}
/**
 * Render an in-editor TC PDF preview from the supplied state. See the
 * standard-reports counterpart for the body shape (the TC endpoint
 * accepts the same JSON contract).
 */
export async function getTcReportPdfPreview(reportId, body) {
    const token = getAccessToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token)
        headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${resolveApiBase(BASE)}${BASE}/${reportId}/pdf-preview`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new ApiError(response.status, await response.text().catch(() => 'PDF preview failed'));
    }
    return await response.blob();
}
// ============ Images (GCS) ============
export async function uploadTcReportImage(reportId, imageId, blob) {
    const formData = new FormData();
    formData.append('reportId', reportId);
    formData.append('imageId', imageId);
    formData.append('file', blob, `${imageId}.png`);
    const result = await apiUpload('POST', `${BASE}/images`, formData);
    return result.storagePath;
}
export function getTcReportImageUrl(reportId, imageId) {
    return `${resolveApiBase(BASE)}${BASE}/images/${reportId}/${imageId}.png`;
}
